import { useState } from 'react'
import { useThemeMode } from '@/contexts/ThemeContext'
import type { ToolInvocation } from '@/types/chat/ChatMessage'

interface ToolInvocationsProps {
  toolInvocations: ToolInvocation[]
}

export function ToolInvocations({ toolInvocations }: ToolInvocationsProps) {
  const { colors } = useThemeMode()
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set())

  if (!toolInvocations || toolInvocations.length === 0) {
    return null
  }

  const toggleExpanded = (toolCallId: string) => {
    setExpandedTools((prev) => {
      const next = new Set(prev)
      if (next.has(toolCallId)) {
        next.delete(toolCallId)
      } else {
        next.add(toolCallId)
      }
      return next
    })
  }

  const getToolDescription = (toolName: string, input: any, output: any) => {
    // Generate user-friendly descriptions based on tool type
    switch (toolName) {
      case 'search_marketplace':
        const listingCount = output?.listings?.length || output?.total || 0
        return `Found ${listingCount} marketplace item${listingCount !== 1 ? 's' : ''}`
      case 'search':
        const resultCount = output?.results?.length || 0
        return `Found ${resultCount} web result${resultCount !== 1 ? 's' : ''}`
      case 'create_artifact':
        return `Created: ${output?.name || 'artifact'}`
      case 'update_artifact':
        return `Updated: ${output?.name || input?.name || 'artifact'}`
      case 'list_artifacts':
        const artifactCount = output?.artifacts?.length || 0
        return `Listed ${artifactCount} artifact${artifactCount !== 1 ? 's' : ''}`
      case 'read_artifact':
        return `Read: ${output?.name || input?.id}`
      case 'create_theme':
        return `Created theme: ${output?.name || input?.name}`
      case 'update_theme':
        return `Updated theme: ${output?.name || input?.name}`
      case 'list_themes':
        const themeCount = output?.themes?.length || 0
        return `Listed ${themeCount} theme${themeCount !== 1 ? 's' : ''}`
      case 'read_theme':
        return `Read theme: ${output?.name || input?.id}`
      case 'create_identity':
        return `Created identity: ${output?.name || input?.name}`
      case 'update_identity':
        return `Updated identity: ${output?.name || input?.name}`
      case 'list_identities':
        const identityCount = output?.identities?.length || 0
        return `Listed ${identityCount} identit${identityCount !== 1 ? 'ies' : 'y'}`
      case 'read_identity':
        return `Read identity: ${output?.name || input?.id}`
      case 'purchase_marketplace_item':
        return output?.success
          ? 'Purchase completed'
          : 'Purchase failed'
      case 'list_item_for_sale':
        return `Listed for $${output?.price?.toFixed(2) || input?.price_usd?.toFixed(2)}`
      case 'unlist_item':
        return 'Removed from marketplace'
      default:
        return `Used ${toolName}`
    }
  }

  return (
    <div
      style={{
        marginTop: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {toolInvocations.map((invocation) => {
        const isExpanded = expandedTools.has(invocation.toolCallId)
        const description = getToolDescription(
          invocation.toolName,
          invocation.input,
          invocation.output
        )

        return (
          <div
            key={invocation.toolCallId}
            style={{
              backgroundColor: colors.secondaryBg,
              border: `1px solid ${colors.secondaryBorder}`,
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            {/* Collapsed summary */}
            <button
              onClick={() => toggleExpanded(invocation.toolCallId)}
              style={{
                width: '100%',
                padding: '8px 10px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: colors.primaryText,
                fontSize: '12px',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryBg
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span
                  style={{
                    fontSize: '14px',
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                >
                  ▶
                </span>
                <span style={{ fontWeight: '500' }}>🔧 {invocation.toolName}</span>
                <span style={{ opacity: 0.7 }}>→ {description}</span>
              </span>
              <span
                style={{
                  fontSize: '10px',
                  opacity: 0.5,
                }}
              >
                {isExpanded ? 'collapse' : 'expand'}
              </span>
            </button>

            {/* Expanded details */}
            {isExpanded && (
              <div
                style={{
                  padding: '10px',
                  backgroundColor: colors.primaryBg,
                  borderTop: `1px solid ${colors.secondaryBorder}`,
                  fontSize: '11px',
                  fontFamily: 'monospace',
                }}
              >
                <div style={{ marginBottom: '10px' }}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      marginBottom: '4px',
                      color: colors.secondaryText,
                    }}
                  >
                    Input:
                  </div>
                  <pre
                    style={{
                      backgroundColor: colors.terminalBg,
                      padding: '8px',
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '200px',
                      margin: 0,
                      color: colors.primaryText,
                    }}
                  >
                    {JSON.stringify(invocation.input, null, 2)}
                  </pre>
                </div>

                {invocation.output != null && (
                  <div>
                    <div
                      style={{
                        fontWeight: 'bold',
                        marginBottom: '4px',
                        color: colors.secondaryText,
                      }}
                    >
                      Output:
                    </div>
                    <pre
                      style={{
                        backgroundColor: colors.terminalBg,
                        padding: '8px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '200px',
                        margin: 0,
                        color: colors.primaryText,
                      }}
                    >
                      {JSON.stringify(invocation.output, null, 2)}
                    </pre>
                  </div>
                )}

                <div
                  style={{
                    marginTop: '8px',
                    fontSize: '10px',
                    opacity: 0.5,
                    color: colors.secondaryText,
                  }}
                >
                  Executed: {new Date(invocation.timestamp).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
